<div mat-dialog-title class="dialog-header-container">
    <div class="header-content">
        <h2 >{{ title }}</h2>
        <span class="mat-font-body-md">{{description}}</span>
    </div>
</div>
<mat-dialog-content>
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedIndexChange)="onTabChange($event)" class="search-dialog-tabs">
        <!-- Map Selection Tab -->
        <mat-tab [tabIndex]="0" label="Select on Map" *ngIf="canUseMapSelection">
            <div class="tab-content">
                <div class="method-description">
                    <mat-icon>map</mat-icon>
                    <span>Use interactive map tools to define your search area</span>
                </div>

                <div class="map-selection-section">
                    <div class="selection-tools">
                        <div class="tool-cards">
                            <mat-card class="tool-card" [class.selected]="selectedMapTool === MapSelectionType.CIRCLE" (click)="selectMapTool(MapSelectionType.CIRCLE)">
                                <mat-card-content>
                                    <mat-icon svgIcon="circle"></mat-icon>
                                    <div class="tool-info">
                                        <div class="tool-title">Point + Radius</div>
                                        <div class="tool-description">Select a point and define search radius</div>
                                    </div>
                                </mat-card-content>
                            </mat-card>

                            <mat-card class="tool-card" [class.selected]="selectedMapTool === MapSelectionType.SQUARE" (click)="selectMapTool(MapSelectionType.SQUARE)">
                                <mat-card-content>
                                    <mat-icon svgIcon="box"></mat-icon>
                                    <div class="tool-info">
                                        <div class="tool-title">Rectangle</div>
                                        <div class="tool-description">Draw a rectangular search area</div>
                                    </div>
                                </mat-card-content>
                            </mat-card>

                            <mat-card class="tool-card" [class.selected]="selectedMapTool === MapSelectionType.POLYGON" (click)="selectMapTool(MapSelectionType.POLYGON)">
                                <mat-card-content>
                                    <mat-icon svgIcon="poly"></mat-icon>
                                    <div class="tool-info">
                                        <div class="tool-title">Custom Polygon</div>
                                        <div class="tool-description">Draw a custom polygon shape</div>
                                    </div>
                                </mat-card-content>
                            </mat-card>
                        </div>

                        <button matButton="filled" color="primary" [disabled]="!selectedMapTool" (click)="startMapSelection()">
                            <mat-icon>edit_location</mat-icon>
                            Start {{ getMapToolLabel(selectedMapTool) }} Selection
                        </button>
                    </div>
                </div>

                <!-- Only show validation status when there's actual map geometry -->
                <div class="tab-validation-status" [class.valid]="mapGeometry?.isValid" [class.invalid]="mapGeometry && !mapGeometry.isValid" *ngIf="mapGeometry">
                    <mat-icon>{{ mapGeometry?.isValid ? "check_circle" : "error" }}</mat-icon>
                    <span *ngIf="mapGeometry?.isValid">Map geometry is valid and ready</span>
                    <span *ngIf="mapGeometry && !mapGeometry.isValid">{{ mapGeometry.error }}</span>
                </div>
            </div>
        </mat-tab>
        <!-- File Upload Tab -->
        <mat-tab [tabIndex]="1" label="Upload File">
            <div class="tab-content">
                <div class="method-description">
                    <mat-icon>file_upload</mat-icon>
                    <span>{{ fileUploadDescription }}</span>
                </div>

                <div class="upload-section">
                    <input #fileInput type="file" class="file-input-hidden" [accept]="acceptedFileTypes" (change)="onFileSelected($event)" />

                    <div
                            class="dropzone d-flex align-items-center justify-content-center text-center"
                            [class.drag-over]="isDragOver"
                            [class.has-file]="uploadedFile"
                            [class.dropzone-error]="fileProcessingState === 'error'"
                            (dragover)="onDragOver($event)"
                            (dragleave)="onDragLeave($event)"
                            (drop)="onDrop($event)"
                            (click)="fileInput.click()">
                        <div *ngIf="!uploadedFile && !isDragOver" class="dropzone-content">
                            <mat-icon class="upload-icon">cloud_upload</mat-icon>
                            <p class="primary-text">Drag & drop your geometry file here</p>
                            <p class="secondary-text">
                                or
                                <span class="link-text">click to browse</span>
                            </p>
                            <div class="file-restrictions">
                                <small>Supported: {{ fileUploadDescription }} <br>• Max size: {{ maxFileSizeFormatted }}</small>
                            </div>
                        </div>

                        <div *ngIf="isDragOver" class="dropzone-drag-over">
                            <mat-icon class="drag-icon">backup</mat-icon>
                            <p>Drop your geometry file here</p>
                        </div>

                        <div *ngIf="uploadedFile" class="uploaded-file-info">
                            <div class="file-icon">
                                <mat-icon>description</mat-icon>
                            </div>
                            <div class="file-details">
                                <div class="file-name">{{ uploadedFile.name }}</div>
                                <div class="file-size">{{ formatFileSize(uploadedFile.size) }}</div>
                                <div *ngIf="fileProcessingState === 'processing'" class="file-status processing">
                                    <mat-progress-spinner diameter="16" mode="indeterminate"></mat-progress-spinner>
                                    <span>Processing...</span>
                                </div>
                                <div *ngIf="fileProcessingState === 'success'" class="file-status success">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Geometry loaded successfully</span>
                                </div>
                                <div *ngIf="fileProcessingState === 'error'" class="file-status error">
                                    <mat-icon>error</mat-icon>
                                    <span>{{ fileError }}</span>
                                </div>
                            </div>
                            <button matIconButton color="warn" (click)="removeFile($event)" matTooltip="Remove file">
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </mat-tab>

        <!-- Text Input Tab -->
        <mat-tab [tabIndex]="2" label="Paste Text">
            <div class="tab-content">
                <div class="method-description">
                    <mat-icon>text_fields</mat-icon>
                    <span>Paste WKT or GeoJSON geometry text directly (only polygon support)</span>
                </div>

                <form [formGroup]="textForm" class="form-section">
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Geometry Text (WKT or GeoJSON geometry)</mat-label>
                        <textarea
                                matInput
                                rows="6"
                                placeholder='POLYGON((lon lat, lon lat, ...)) or {"type": "Polygon", ...}'
                                formControlName="geometryText"
                                (input)="onGeometryTextChange()"></textarea>
                        <mat-hint>Paste your WKT or GeoJSON geometry here</mat-hint>
                        <mat-error *ngIf="textForm.get('geometryText')?.errors?.['required']">Geometry text is required</mat-error>
                        <mat-error *ngIf="textForm.get('geometryText')?.errors?.['invalidText']">
                            Invalid geometry format. Please check your WKT or GeoJSON syntax.
                        </mat-error>
                    </mat-form-field>
                </form>
            </div>
        </mat-tab>

        <!-- Layer on Map Tab -->
        @if (availableLayers.length > 0) {
            <mat-tab [tabIndex]="3" label="Layer on Map">
                <div class="tab-content">
                    <div class="method-description">
                        <mat-icon>layers</mat-icon>
                        <span>Select a My Data layer to use its geometry as the geofencing area</span>
                    </div>

                    <div class="d-flex flex-column gap-2">
                        @for (layer of availableLayers; track layer.id) {
                            <div
                                class="layer-list-item d-flex align-items-center gap-3 rounded-2 px-3 py-2"
                                [class.selected]="selectedLayerId === layer.id"
                                (click)="onLayerSelected(layer)"
                                role="button"
                                tabindex="0">
                                <mat-icon class="flex-shrink-0">insert_drive_file</mat-icon>
                                <span class="flex-grow-1 overflow-hidden text-truncate">{{ layer.name }}</span>
                                @if (getLayerFileExt(layer.name)) {
                                    <span class="layer-ext-badge flex-shrink-0">{{ getLayerFileExt(layer.name) }}</span>
                                }
                                @if (selectedLayerId === layer.id) {
                                    <mat-icon class="flex-shrink-0 layer-check-icon">check_circle</mat-icon>
                                }
                            </div>
                        }
                    </div>

                    @if (layerGeometry) {
                        <div class="tab-validation-status"
                             [class.valid]="layerGeometry.isValid"
                             [class.invalid]="!layerGeometry.isValid">
                            <mat-icon>{{ layerGeometry.isValid ? "check_circle" : "error" }}</mat-icon>
                            @if (layerGeometry.isValid) {
                                <span>Layer geometry is valid and ready</span>
                            } @else {
                                <span>{{ layerGeometry.error }}</span>
                            }
                        </div>
                    }
                </div>
            </mat-tab>
        }
    </mat-tab-group>

    <!-- Radius Configuration (only for point geometry and only in Select on Map tab) -->
    <div *ngIf="currentGeometry?.isCircleGeometry && isMapTab" class="radius-section">
        <form [formGroup]="radiusForm">
            <mat-form-field>
                <mat-label>Radius</mat-label>
                <input matInput type="number" formControlName="radius" min="1" [readonly]="true" />
                <span matSuffix>meters</span>
                <mat-hint>Search radius around the point</mat-hint>
                <mat-error *ngIf="radiusForm.get('radius')?.hasError('required')">Radius is required</mat-error>
                <mat-error *ngIf="radiusForm.get('radius')?.hasError('min')">Minimum radius is 1 meter</mat-error>
            </mat-form-field>
        </form>
    </div>

    <!-- Global error from geofencing apply (search API validation) -->
    @if (globalError) {
        <div class="tab-validation-status invalid d-flex align-items-center gap-2">
            <mat-icon>error</mat-icon>
            <span>{{ globalError }}</span>
        </div>
    }

    <!-- Area measurement: the embeddings API rejects geofencing areas above maxAreaLabel.
         Not shown at all when enforceAreaLimit is off — that limit doesn't apply. -->
    @if (enforceAreaLimit && currentGeometry?.isValid && geometryArea) {
        <div class="tab-validation-status" [class.valid]="!areaExceedsLimit" [class.invalid]="areaExceedsLimit">
            <mat-icon>{{ areaExceedsLimit ? "warning" : "straighten" }}</mat-icon>
            @if (areaExceedsLimit) {
                <span>{{ geometryValidation.reason }}</span>
            } @else {
                <span>Geofencing area: <strong>{{ geometryArea.formatted }}</strong> (maximum {{ maxAreaLabel }})</span>
            }
        </div>
    }

</mat-dialog-content>

<!-- Dialog Actions -->
<mat-dialog-actions align="end">
    @if (mapGeometry) {
        <button matButton="filled" color="primary" (click)="onDelete()">
            <mat-icon>delete</mat-icon>
            Delete
        </button>
    }
    <div class="spacer"></div>
    <button matButton (click)="onCancel()">Cancel</button>

    <div [matTooltip]="areaExceedsLimit ? geometryValidation.reason : ''">
        <button matButton="filled" color="primary" [disabled]="!canApplyGeofencing || isProcessing" [class.loading]="isProcessing" (click)="onApply()">
            <mat-spinner diameter="20" *ngIf="isProcessing"></mat-spinner>
            <mat-icon *ngIf="!isProcessing && canApplyGeofencing">check</mat-icon>
            <span *ngIf="!isProcessing">Apply</span>
        </button>
    </div>
</mat-dialog-actions>
